import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ContractModal.css';

interface ContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (contractData: ContractData) => void;
  loading?: boolean;
  initialData?: {
    projectTitle?: string;
    proposedBudget?: number;
    proposedDuration?: number;
    projectId?: number;
    serviceId?: number;
  };
  type: 'project' | 'service';
}

export interface ContractData {
  title: string;
  description: string;
  price: number;
  startDate: string;
  endDate: string;
  deliverables: string;
  projectId?: number;
  serviceId?: number;
}

const ContractModal: React.FC<ContractModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  loading,
  initialData,
  type
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deliverables, setDeliverables] = useState('');

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.projectTitle || '');
      if (initialData.proposedBudget) {
        setPrice(initialData.proposedBudget.toString());
      }
      // 시작일을 오늘로 설정
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      // 종료일을 제안 기간으로 설정
      if (initialData.proposedDuration) {
        const end = new Date();
        end.setDate(end.getDate() + initialData.proposedDuration);
        setEndDate(end.toISOString().split('T')[0]);
      }
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !price || !startDate || !endDate) {
      alert(t('contractModal.requiredFields'));
      return;
    }

    if (new Date(endDate) <= new Date(startDate)) {
      alert(t('contractModal.dateOrder'));
      return;
    }

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      price: parseInt(price),
      startDate,
      endDate,
      deliverables: deliverables.trim(),
      projectId: initialData?.projectId,
      serviceId: initialData?.serviceId
    });
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setStartDate('');
    setEndDate('');
    setDeliverables('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const typeLabel = type === 'project' ? t('contractModal.typeProject') : t('contractModal.typeService');

  return (
    <div className="contract-modal-overlay" onClick={handleClose}>
      <div className="contract-modal" onClick={e => e.stopPropagation()}>
        <div className="contract-modal-header">
          <h2>📝 {t('contractModal.title')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>
        
        <div className="contract-notice">
          <p>⚠️ {t('contractModal.notice', { type: typeLabel })}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('quoteModal.workTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t('contractModal.titlePlaceholder', { type: typeLabel })}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('contractModal.workDesc')}</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('contractModal.descPlaceholder')}
              rows={4}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('quoteModal.priceLabel')}</label>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="100000"
                min="1000"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('contractModal.startDate')}</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('contractModal.endDate')}</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('contractModal.deliverables')}</label>
            <textarea
              value={deliverables}
              onChange={e => setDeliverables(e.target.value)}
              placeholder={t('contractModal.deliverablesPlaceholder')}
              rows={2}
            />
          </div>

          {price && startDate && endDate && (
            <div className="contract-preview">
              <h4>{t('contractModal.summary')}</h4>
              <div className="preview-item">
                <span>{t('contractModal.totalPrice')}</span>
                <strong>{t('projects.budgetExact', { v: parseInt(price).toLocaleString() })}</strong>
              </div>
              <div className="preview-item">
                <span>{t('contractModal.period')}</span>
                <strong>{startDate} ~ {endDate}</strong>
              </div>
              <div className="preview-item">
                <span>{t('contractModal.estDays')}</span>
                <strong>{t('projectDetail.daysN', { n: Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) })}</strong>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={handleClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? t('projectDetail.sending') : t('projectDetail.sendProposal')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractModal;
